import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import qs from 'qs';

import CustomTable from 'components/common/customTableV2';
import EmptyData from 'components/common/emptyData';
import Loading from 'components/common/loading';
import PageHeader from 'components/common/pageHeader';
import WineLoversService from 'services/wineLoversService';
import wineLoversStore from 'store/wineLoversStore';
import { adaptWineLoversTableData } from 'helpers/adapter';
import { replacePath, getMessage } from 'helpers/helper';
import { ReactComponent as EmptyUser } from 'assets/emptyUser.svg';
import M from 'messages';

import SidebarStore from 'store/ui/sidebarStore';
import { routes } from 'config';
import tableOptions from './config/config';
import CustomTableToolbar from './toolbar';

import classes from './styles';

const WineLovers = observer(({ path }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const pageParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [loading, setLoading] = useState(true);
  const [fromFilter, setFromFilter] = useState(true);
  const [wineLoversData, setWineLoversData] = useState({});

  const isSidebarOpened = SidebarStore.sidebarState;
  const { searchFields, filterFields } = tableOptions.wineLover;

  // NOTE: cut / symbol
  const pageItem = path.substring(1);

  const setFilter = filter => {
    replacePath(navigate, filter);
    wineLoversStore.setFilter(filter);
  };

  const queryParams = wineLoversStore.getCurrentFilterParams(pageParams);

  const getWineLoversLists = useCallback(
    async (params, withFilter = true) => {
      try {
        // setLoading(true);
        setFromFilter(withFilter);
        const urlParams = params.params;
        if (
          (urlParams?.filter && Object.keys(urlParams.filter).length) ||
          (urlParams?.search && urlParams.search.value.length)
        ) {
          setFromFilter(true);
        }
        setFilter(params);
        const newParams = { params: { ...params.params } };
        const response = await WineLoversService.getWineLovers(newParams);
        if (response) {
          wineLoversStore.setStoreWineLovers(response.data);
          setWineLoversData({
            count: response.data.count,
            data: adaptWineLoversTableData(response.data.data),
          });
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar],
  );

  useEffect(() => {
    getWineLoversLists(queryParams, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearchCallback = value => {
    let oldValue = queryParams.params?.search?.value;
    if (oldValue === undefined) {
      oldValue = '';
    }
    if (searchFields && oldValue !== value) {
      const newFilter = {
        params: {
          ...queryParams.params,
          search: { ...queryParams.params.search, value, fields: searchFields },
          skip: 0,
        },
      };
      if (!value) {
        delete newFilter.params.search;
      }
      setFilter(newFilter);
      getWineLoversLists(newFilter, true);
    }
  };

  const handleExport = async () => {
    const { params } = wineLoversStore.filter;
    delete params.limit;
    delete params.skip;
    WineLoversService.exportWineLovers(params);
  };

  const onFilterCallback = (filterObj, reset = false) => {
    if (reset) {
      wineLoversStore.resetFilters();
      const newParams = wineLoversStore.getCurrentFilterParams();
      setLoading(true);
      getWineLoversLists(newParams, false);
      return;
    }
    if (filterObj && !Array.isArray(filterObj)) {
      const fieldKeyList = Object.keys(filterObj);
      let newFilter = {};
      fieldKeyList.forEach(i => {
        const fieldValue = filterObj[i];
        let oldValue = queryParams.params?.filter?.[i];
        if (oldValue === undefined) {
          oldValue = '';
        }
        if (filterFields && oldValue !== fieldValue && fieldValue !== undefined) {
          newFilter = {
            ...newFilter,
            params: {
              ...queryParams.params,
              ...newFilter.params,
              filter: { ...queryParams.params.filter, ...newFilter?.params?.filter, [i]: fieldValue },
              skip: 0,
            },
          };
        }
      });
      if (Object.keys(newFilter).length) {
        setFilter(newFilter);
        getWineLoversLists(newFilter);
      }
    }
  };

  const toolbarView = (
    <CustomTableToolbar
      onSearchCallback={onSearchCallback}
      filteredParams={queryParams}
      handleExport={wineLoversData.count > 0 ? handleExport : null}
      onFilterCallback={onFilterCallback}
      filterFields={filterFields}
    />
  );

  const openPath = row => routes.wineLover.path.replace(':id', row.id);

  const isEmptyState = () => !fromFilter && !wineLoversData?.data?.length;

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Box
        sx={{
          ...classes.root,
          ...(isSidebarOpened && classes.rootDrawerOpen),
          ...(!isSidebarOpened && classes.rootDrawerClose),
        }}>
        <PageHeader title={M.get('wineLovers.pageTitle')} onlyGeneralPageTitle />
        <Grid size={{ xs: 12 }}>
          <Box sx={classes.tableRoot}>
            {isEmptyState() && !loading ? (
              <EmptyData
                icon={<EmptyUser />}
                title={pageItem}
                titleClassName="userEmptyPageTitle"
                mainClassName="userMain"
                withCreate={false}
              />
            ) : (
              <Grid container sx={classes.container}>
                <Box sx={classes.tabsContainer}>
                  <CustomTable
                    tableSources={wineLoversData}
                    tableOptions={tableOptions.wineLover}
                    loading={loading}
                    filteredParams={queryParams}
                    setFilteredParams={setFilter}
                    getDataOnChange={getWineLoversLists}
                    openPath={openPath}
                    toolbarView={toolbarView}
                    withEditAction={false}
                    withCheckbox={false}
                  />
                </Box>
              </Grid>
            )}
          </Box>
        </Grid>
      </Box>
    </>
  );
});

WineLovers.propTypes = {
  path: PropTypes.string.isRequired,
};

export default WineLovers;
